import axios from "axios";
import jwtDecode from "jwt-decode";
import { encryptStorage } from "./EncryptStorage";
import getUrl from "./urlConst";

const REFRESH_LOCK_NAME = "erp-auth-token-refresh";
const MINIMUM_TOKEN_VALIDITY_MS = 30 * 1000;

let refreshPromise = null;

function getStoredTokens() {
  return {
    token: encryptStorage.getItem("token"),
    refreshToken: encryptStorage.getItem("refreshToken"),
  };
}

function isTokenValid(token) {
  if (!token) return false;

  try {
    return jwtDecode(token).exp * 1000 - Date.now() > MINIMUM_TOKEN_VALIDITY_MS;
  } catch {
    return false;
  }
}

function getReplacementToken(requestedRefreshToken) {
  const currentTokens = getStoredTokens();

  if (
    currentTokens.refreshToken !== requestedRefreshToken &&
    isTokenValid(currentTokens.token)
  ) {
    return currentTokens.token;
  }

  return null;
}

async function requestNewToken(requestedRefreshToken) {
  const replacementToken = getReplacementToken(requestedRefreshToken);
  if (replacementToken) return replacementToken;

  const values = getStoredTokens();
  if (!values.token || !values.refreshToken) {
    throw new Error("Authentication tokens are missing.");
  }

  let response;
  try {
    response = await axios.post(`${getUrl()}api/account/refreshtoken`, values);
  } catch (error) {
    const tokenFromAnotherTab = getReplacementToken(values.refreshToken);
    if (tokenFromAnotherTab) return tokenFromAnotherTab;
    throw error;
  }

  if (!response.data.success) {
    const tokenFromAnotherTab = getReplacementToken(values.refreshToken);
    if (tokenFromAnotherTab) return tokenFromAnotherTab;

    throw new Error(
      response.data.errors?.[0] || "The authentication token could not be refreshed."
    );
  }

  // Do not let a delayed response overwrite a newer pair written by another tab.
  if (encryptStorage.getItem("refreshToken") !== values.refreshToken) {
    return encryptStorage.getItem("token");
  }

  encryptStorage.setItem("token", JSON.stringify(response.data.token));
  encryptStorage.setItem(
    "refreshToken",
    JSON.stringify(response.data.refreshToken)
  );

  return response.data.token;
}

async function refreshAcrossTabs(requestedRefreshToken) {
  if (navigator.locks?.request) {
    return navigator.locks.request(REFRESH_LOCK_NAME, () =>
      requestNewToken(requestedRefreshToken)
    );
  }

  return requestNewToken(requestedRefreshToken);
}

export function RefreshToken() {
  if (!refreshPromise) {
    const requestedRefreshToken = encryptStorage.getItem("refreshToken");
    refreshPromise = refreshAcrossTabs(requestedRefreshToken).finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}
